import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  Icon,
  useToast,
  Image,
  Grid,
  FormControl,
  FormLabel,
  Input,
  Link,
  Spinner,
  useColorModeValue,
  Container,
  HStack,
  Divider,
  InputGroup,
  InputLeftElement,
  IconButton
} from '@chakra-ui/react';
import { WarningIcon, EmailIcon, LockIcon, ArrowForwardIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { FaSignInAlt } from 'react-icons/fa';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import AuthContext from '../services/AuthContext';

// Color constants
const primaryColor = '#F2C919';
const secondaryColor = '#2C3E50';
const hoverColor = '#E5B817';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const imageVariants = {
  hidden: { x: -50, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const toast = useToast();
  const { login, isVerifying } = useContext(AuthContext);

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(email, password);
      // The AuthContext will handle the navigation after successful login
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'حدث خطأ أثناء تسجيل الدخول';
      setError(errorMessage);
      toast({
        title: 'خطأ في تسجيل الدخول',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={bgColor}>
        <Spinner size="xl" color={primaryColor} />
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor} dir="rtl" fontFamily="Cairo, sans-serif">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <Container maxW="8xl" py={8}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={8} minH="80vh" alignItems="center">
            {/* Left Side - Image */}
            <motion.div variants={imageVariants}>
              <Box display={{ base: 'none', lg: 'block' }}>
                <Image
                  src="/assets/login.jpg"
                  alt="تسجيل الدخول"
                  w="100%"
                  h="600px"
                  objectFit="cover"
                  borderRadius="2xl"
                  boxShadow="2xl"
                />
              </Box>
            </motion.div>

            {/* Right Side - Login Form */}
            <motion.div variants={itemVariants}>
              <Box
                bg={cardBg}
                p={8}
                borderRadius="2xl"
                boxShadow="2xl"
                border="1px solid"
                borderColor="gray.200"
                maxW="500px"
                mx="auto"
              >
                <VStack spacing={6} align="stretch">
                  {/* Logo and Title */}
                  <VStack spacing={4}>
                    <Image
                      src="/assets/logo.png"
                      alt="كوبرا للتعليم"
                      h="80px"
                      objectFit="contain"
                    />
                    <Heading
                      as="h1"
                      size="xl"
                      color={secondaryColor}
                      textAlign="center"
                      fontWeight="bold"
                    >
                      تسجيل الدخول
                    </Heading>
                    <Text color="gray.600" textAlign="center" fontSize="md">
                      ادخل إلى حسابك للوصول إلى دوراتك ومتابعة تعلمك
                    </Text>
                  </VStack>

                  {/* Error Message */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box
                        bg="red.50"
                        border="1px solid"
                        borderColor="red.200"
                        borderRadius="lg"
                        p={3}
                      >
                        <HStack>
                          <Icon as={WarningIcon} color="red.500" />
                          <Text color="red.700" fontSize="sm">
                            {error}
                          </Text>
                        </HStack>
                      </Box>
                    </motion.div>
                  )}

                  {/* Login Form */}
                  <form onSubmit={handleSubmit}>
                    <VStack spacing={4}>
                      <FormControl isRequired>
                        <FormLabel color={secondaryColor} fontWeight="medium">
                          البريد الإلكتروني
                        </FormLabel>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <EmailIcon color="gray.400" />
                          </InputLeftElement>
                          <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="أدخل بريدك الإلكتروني"
                            bg="gray.50"
                            border="2px solid"
                            borderColor="gray.200"
                            _focus={{
                              borderColor: primaryColor,
                              boxShadow: `0 0 0 1px ${primaryColor}`,
                              bg: "white"
                            }}
                            _hover={{ borderColor: "gray.300" }}
                            borderRadius="lg"
                            h="50px"
                          />
                        </InputGroup>
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel color={secondaryColor} fontWeight="medium">
                          كلمة المرور
                        </FormLabel>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <LockIcon color="gray.400" />
                          </InputLeftElement>
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="أدخل كلمة المرور"
                            bg="gray.50"
                            border="2px solid"
                            borderColor="gray.200"
                            _focus={{
                              borderColor: primaryColor,
                              boxShadow: `0 0 0 1px ${primaryColor}`,
                              bg: "white"
                            }}
                            _hover={{ borderColor: "gray.300" }}
                            borderRadius="lg"
                            h="50px"
                            pr="50px"
                          />
                          <IconButton
                            aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            onClick={() => setShowPassword(!showPassword)}
                            variant="ghost"
                            position="absolute"
                            right="8px"
                            top="50%"
                            transform="translateY(-50%)"
                            zIndex={2}
                            color="gray.500"
                            _hover={{ color: primaryColor }}
                          />
                        </InputGroup>
                      </FormControl>

                      <Flex justify="space-between" align="center" w="100%">
                        <Link
                          as={RouterLink}
                          to="/forgot-password"
                          color={primaryColor}
                          fontSize="sm"
                          _hover={{ color: hoverColor, textDecoration: 'underline' }}
                        >
                          نسيت كلمة المرور؟
                        </Link>
                      </Flex>

                      <Button
                        type="submit"
                        bg={primaryColor}
                        color={secondaryColor}
                        size="lg"
                        w="100%"
                        h="50px"
                        borderRadius="lg"
                        fontWeight="bold"
                        fontSize="md"
                        _hover={{
                          bg: hoverColor,
                          transform: 'translateY(-2px)',
                          boxShadow: 'lg'
                        }}
                        _active={{
                          transform: 'translateY(0)',
                          boxShadow: 'md'
                        }}
                        transition="all 0.2s"
                        isLoading={isLoading}
                        loadingText="جاري تسجيل الدخول..."
                        leftIcon={!isLoading ? <FaSignInAlt /> : undefined}
                      >
                        {isLoading ? <Spinner size="sm" /> : 'تسجيل الدخول'}
                      </Button>
                    </VStack>
                  </form>

                  <Divider />

                  {/* Back to Home */}
                  <VStack spacing={2}>
                    <Divider />
                    <Button
                      as={RouterLink}
                      to="/"
                      variant="ghost"
                      color="gray.600"
                      size="sm"
                      _hover={{ color: primaryColor }}
                    >
                      العودة إلى الصفحة الرئيسية
                    </Button>
                  </VStack>
                </VStack>
              </Box>
            </motion.div>
          </Grid>
        </motion.div>
      </Container>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default LoginPage;